<template>
  <div class="card m-3 text-center">
    <h4 class="card-header">{{ t('set-new-password') }}</h4>
    <div class="d-flex card-body justify-content-center">
      <Form
        @submit="onSubmit"
        :validation-schema="schema"
        :initial-values="initialValues"
        v-slot="{ errors, isSubmitting }"
      >
        <!-- Pole na token. Ukrywamy, gdy token przyszedł w URL (link z e-maila). -->
        <Field name="token" type="hidden" v-if="tokenFromUrl" />
        <div class="form-group row mb-2" v-else>
          <label class="col-sm-4 col-form-label">{{ t('token') }}</label>
          <div class="col-sm-8">
            <Field
              name="token"
              type="text"
              class="form-control"
              :class="{ 'is-invalid': errors.token }"
            />
            <div class="invalid-feedback">{{ errors.token }}</div>
          </div>
        </div>

        <!-- Nowe hasło -->
        <div class="form-group row mb-2">
          <label class="col-sm-4 col-form-label">{{ t('new-password') }}</label>
          <div class="col-sm-8">
            <Field
              name="newPassword"
              type="password"
              class="form-control"
              :class="{ 'is-invalid': errors.newPassword }"
            />
            <div class="invalid-feedback">{{ errors.newPassword }}</div>
          </div>
        </div>

        <!-- Wymagania wobec hasła — te same, które sprawdza backend. Pokazane
             od razu, żeby użytkownik nie zgadywał, dlaczego formularz go nie puszcza. -->
        <div class="form-group row mb-2">
          <div class="col-sm-8 offset-sm-4 password-hint">
            {{ t('password-policy-hint', { length: MIN_PASSWORD_LENGTH }) }}
          </div>
        </div>

        <!-- Powtórzenie nowego hasła -->
        <div class="form-group row mb-3">
          <label class="col-sm-4 col-form-label">{{ t('confirm-password') }}</label>
          <div class="col-sm-8">
            <Field
              name="confirmPassword"
              type="password"
              class="form-control"
              :class="{ 'is-invalid': errors.confirmPassword }"
            />
            <div class="invalid-feedback">{{ errors.confirmPassword }}</div>
          </div>
        </div>

        <!-- Przycisk zatwierdzenia. Wynik operacji pokazuje globalne powiadomienie. -->
        <div class="form-group d-flex flex-column align-items-center">
          <div class="col-auto">
            <button
              class="btn btn-outline-secondary mt-2"
              :disabled="isSubmitting"
            >
              <span
                v-show="isSubmitting"
                class="spinner-border spinner-border-sm mr-1"
              ></span>
              {{ t('change-password') }}
            </button>
          </div>
        </div>
      </Form>
    </div>
  </div>
</template>

<script setup>
// Importy potrzebnych bibliotek i narzędzi
import { computed, onMounted, onBeforeUnmount } from "vue";
import { Form, Field } from "vee-validate";
import * as yup from "yup";
import { useAuthStore } from "@/stores/auth.store";
import { useRoute } from "vue-router";
import router from "@/router";
import { useI18n } from 'vue-i18n';
import { useNotificationStore, stripFieldPath } from "@/stores/notification.store";
import { applyPasswordPolicy, MIN_PASSWORD_LENGTH } from "@/helpers/passwordPolicy";

const route = useRoute();

// Token resetujący przekazywany w query stringu linku z wiadomości e-mail.
// Identyfikacja użytkownika odbywa się wyłącznie po stronie BE na podstawie tokenu —
// adresu email celowo nie ma w URL (PII w logach/historii/refererze).
// Gdy token obecny — pole tokena jest ukrywane i pre-wypełniane.
const tokenFromUrl = computed(() => route.query.token || "");
const initialValues = computed(() => ({ token: tokenFromUrl.value }));

// Defense-in-depth: zapobiegaj wyciekowi tokenu w nagłówku Referer, gdyby z tego widoku
// kliknięto cokolwiek na zewnątrz. Tag wstawiamy tylko na czas życia komponentu.
const REFERRER_META_ID = "reset-password-no-referrer";
onMounted(() => {
  if (document.getElementById(REFERRER_META_ID)) return;
  const meta = document.createElement("meta");
  meta.id = REFERRER_META_ID;
  meta.name = "referrer";
  meta.content = "no-referrer";
  document.head.appendChild(meta);
});
onBeforeUnmount(() => {
  document.getElementById(REFERRER_META_ID)?.remove();
});

const { t } = useI18n();
const notify = useNotificationStore();

// Walidacja formularza
const schema = yup.object({
  token: yup.string().required(t('token-required')),
  newPassword: applyPasswordPolicy(
    yup.string().required(t('user-password-required')),
    t
  ),
  confirmPassword: yup
    .string()
    .oneOf([yup.ref("newPassword")], t('passwords-must-be-the-same'))
    .required(t('password-confirmation-required')),
});

// Logika wysyłki formularza
const onSubmit = async (values) => {
  try {
    const authStore = useAuthStore();

    // Wywołanie akcji zmiany hasła z przekazaniem nowego hasła i tokena.
    // Email nie jest przekazywany — backend wyciąga usera z tokenu.
    const response = await authStore.setPassword(
      values.newPassword,
      values.token
    );

    // `setPassword` zwraca null przy powodzeniu (HTTP 204, brak treści), a przy
    // niepowodzeniu to, czym odrzucił `fetch-wrapper`: string "pole: komunikat"
    // (np. złamana polityka haseł, wygasły token) albo goły numer statusu HTTP.
    if (response !== null) {
      notify.error(extractUserMessage(response));
      return;
    }

    // Sukces — potwierdzenie idzie przez globalne powiadomienie, więc jest widoczne
    // także po przekierowaniu na ekran logowania.
    notify.success(t('password-changed'));
    router.push("/login");
  } catch (err) {
    notify.error(err);
    console.error("Błąd HTTP:", err);
  }
};

/**
 * Wyciąga czytelną część komunikatu z odrzucenia `fetch-wrapper`. Nazwę pola
 * ("password: ...") ścina wspólny `stripFieldPath`; dla gołego statusu HTTP
 * (np. 500) pokazujemy komunikat ogólny, bo numer nic użytkownikowi nie mówi.
 */
function extractUserMessage(response) {
  if (typeof response !== "string") return t('password-change-failed');
  return stripFieldPath(response) || t('password-change-failed');
}
</script>

<style scoped>
.card-body {
  background: var(--surface-secondary);
}
.card-body form {
  width: 560px;
}
.col-form-label {
  text-align: right;
  line-height: 25px;
}
.form-group {
  font-size: var(--font-size-sm);
}
button:not(.btn-close) {
  border: 1px solid grey;
  color: var(--text-primary);
  font-size: var(--font-size-lg);
  font-weight: var(--font-weight-bold);
}
a {
  color: var(--text-primary);
  font-size: var(--font-size-sm);
  text-decoration: none;
}
input {
  border: 0;
  border-left: 1px solid var(--border-default);
  border-top: 1px solid var(--border-default);
  padding: 10px;
}
input:focus {
  border-color: var(--border-default);
  box-shadow: 0 0 0 0.25rem rgba(100, 101, 104, 0.25);
}
.password-hint {
  text-align: left;
  font-size: var(--font-size-xs);
  color: var(--text-secondary);
  line-height: var(--line-height-normal);
}
</style>
